import React from 'react';
import {
  Card,
  Group,
  Stack,
  Text,
  Badge,
  ActionIcon,
  Tooltip,
  Box,
  ThemeIcon,
  Divider
} from '@mantine/core';
import {
  IconMapPin,
  IconWorld,
  IconEdit,
  IconTrash,
  IconCheck,
  IconX,
  IconBrandReddit
} from '@tabler/icons-react';
import { RegionCardProps } from './RegionCard.types';

/**
 * RegionCard Component
 *
 * Displays a region summary in a card format
 *
 * Features:
 * - Region name and location (country, state, city)
 * - SubArea count
 * - Subreddit count
 * - Active status badge
 * - Edit/Delete actions
 * - Compact and detailed modes
 *
 * Usage:
 * ```tsx
 * <RegionCard
 *   region={regionData}
 *   showActions
 *   onEdit={handleEdit}
 *   onDelete={handleDelete}
 * />
 * ```
 */
export const RegionCard: React.FC<RegionCardProps> = ({
  region,
  onClick,
  onEdit,
  onDelete,
  showActions = false,
  compact = false,
  detailed = false
}): React.ReactElement => {
  // Format location string
  const locationString = [region.city, region.state, region.country]
    .filter(Boolean)
    .join(', ');

  // Handle card click
  const handleClick = (): void => {
    if (onClick) {
      onClick(region);
    }
  };

  // Handle edit
  const handleEdit = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(region);
    }
  };

  // Handle delete
  const handleDelete = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(region.id);
    }
  };

  return (
    <Card
      withBorder
      padding={compact ? 'sm' : 'md'}
      shadow="sm"
      onClick={handleClick}
    >
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="sm">
            <ThemeIcon size="lg" variant="light" color="blue">
              <IconMapPin size={20} />
            </ThemeIcon>
            <Stack gap={4}>
              <Group gap="xs">
                <Text size={compact ? 'md' : 'lg'} fw={600} lineClamp={1}>
                  {region.regionName}
                </Text>
                {region.isActive ? (
                  <Badge color="green" variant="light" size="sm" leftSection={<IconCheck size={10} />}>
                    Active
                  </Badge>
                ) : (
                  <Badge color="gray" variant="light" size="sm" leftSection={<IconX size={10} />}>
                    Inactive
                  </Badge>
                )}
              </Group>
              <Text size="sm" c="dimmed">
                {region.regionId}
              </Text>
            </Stack>
          </Group>

          {/* Actions */}
          {showActions && (
            <Group gap="xs">
              {onEdit && (
                <Tooltip label="Edit region">
                  <ActionIcon variant="subtle" color="blue" onClick={handleEdit}>
                    <IconEdit size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
              {onDelete && (
                <Tooltip label="Delete region">
                  <ActionIcon variant="subtle" color="red" onClick={handleDelete}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          )}
        </Group>

        {/* Location */}
        <Group gap="xs">
          <IconWorld size={14} />
          <Text size="sm">{locationString}</Text>
        </Group>

        {/* SubAreas */}
        {detailed && (
          <Box>
            <Text size="xs" c="dimmed" tt="uppercase" mb={4}>
              SubAreas
            </Text>
            <Text size="sm">
              {region.subAreas?.length || 0} SubArea{region.subAreas?.length !== 1 ? 's' : ''}
            </Text>
          </Box>
        )}

        {/* Subreddits */}
        {region.subreddits.length > 0 && (
          <>
            {detailed && <Divider />}
            <Box>
              <Group gap="xs" mb="xs">
                <IconBrandReddit size={14} />
                <Text size="xs" c="dimmed" tt="uppercase">
                  Subreddits ({region.subreddits.length})
                </Text>
              </Group>
              <Group gap="xs">
                {detailed ? (
                  region.subreddits.map((subreddit) => (
                    <Badge key={subreddit} variant="light" color="orange" size="sm">
                      r/{subreddit}
                    </Badge>
                  ))
                ) : (
                  <Text size="sm" c="dimmed">
                    {region.subreddits.length} subreddit{region.subreddits.length !== 1 ? 's' : ''}{' '}
                    monitored
                  </Text>
                )}
              </Group>
            </Box>
          </>
        )}

        {/* Description */}
        {detailed && region.description && (
          <>
            <Divider />
            <Text size="sm" c="dimmed" lineClamp={3}>
              {region.description}
            </Text>
          </>
        )}
      </Stack>
    </Card>
  );
};
